import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as promptsApi from '../api/aiPrompts';

export interface AiPrompt {
    promptId: string;
    promptName: string;
    promptDescription?: string;
    promptText: string;
    promptType: string;
    createdAt: Date;
    updatedAt: Date;
    auditVersion: number;
}

export const promptsKey = ['prompts'] as const;
export const promptKey = (promptId: string) => ['prompt', promptId] as const;

export const usePrompts = () => {
    return useQuery({
        queryKey: promptsKey,
        queryFn: promptsApi.getPrompts,
    });
};

export const usePrompt = (promptId: string) => {
    return useQuery({
        queryKey: promptKey(promptId),
        queryFn: () => promptsApi.getPromptById(promptId),
        enabled: !!promptId,
    });
};

export const useCreatePrompt = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (promptData: Partial<AiPrompt>) => promptsApi.createPrompt(promptData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: promptsKey });
        },
    });
};

export const useUpdatePrompt = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ promptId, promptData }: { promptId: string; promptData: Partial<AiPrompt> }) =>
            promptsApi.updatePrompt(promptId, promptData),
        onSuccess: (_, { promptId }) => {
            queryClient.invalidateQueries({ queryKey: promptsKey });
            queryClient.invalidateQueries({ queryKey: promptKey(promptId) });
        },
    });
};
